import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Loader2, TrendingUp, Download } from "lucide-react";
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../../lib/api";

function downloadCSV(data, filename) {
  const csv = [Object.keys(data[0]).join(","), ...data.map(r => Object.values(r).join(","))].join("\n");
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: filename });
  a.click(); URL.revokeObjectURL(a.href);
}

export function ForecastTab({ restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [daysAhead, setDaysAhead] = useState(30);

  const load = useCallback((days) => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/analytics/forecast?days_ahead=${days}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => { load(daysAhead); }, [restaurantId, daysAhead, load]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { historical, forecast } = data;

  // Merge for chart
  const chartData = [
    ...historical.map(d => ({ ...d, predicted: null, low: null, high: null, type: "real" })),
    // Seam point
    historical.length > 0 ? { date: forecast[0]?.date, ventas: null, predicted: forecast[0]?.predicted, low: forecast[0]?.low, high: forecast[0]?.high, type: "forecast" } : null,
    ...forecast.map(d => ({ date: d.date, ventas: null, predicted: d.predicted, low: d.low, high: d.high, type: "forecast" })),
  ].filter(Boolean);

  const totalForecast = forecast.reduce((s, d) => s + d.predicted, 0);
  const avgForecast = forecast.length ? totalForecast / forecast.length : 0;
  const totalHistorical = historical.reduce((s, d) => s + d.ventas, 0);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {[7, 14, 30, 60].map(d => (
            <button key={d} onClick={() => setDaysAhead(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${daysAhead === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              data-testid={`forecast-days-${d}`}>
              {d} días
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadCSV(forecast, `prediccion-${daysAhead}dias.csv`)} className="gap-1 text-xs" data-testid="export-forecast-csv">
          <Download className="w-3 h-3" />Exportar CSV
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Previsión total {daysAhead}d</p>
            <p className="text-xl font-bold mt-1">{totalForecast.toFixed(0)}€</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Media diaria prevista</p>
            <p className="text-xl font-bold mt-1">{avgForecast.toFixed(0)}€</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Histórico últimos 30d</p>
            <p className="text-xl font-bold mt-1">{totalHistorical.toFixed(0)}€</p>
          </CardContent>
        </Card>
      </div>

      {/* Combined chart */}
      <Card data-testid="forecast-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            Histórico + Predicción con intervalo de confianza
          </CardTitle>
          <p className="text-xs text-muted-foreground">La zona sombreada muestra el intervalo de confianza del ±1.5σ</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} tickFormatter={d => d.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
              <Tooltip
                formatter={(v, name) => v ? [`${Number(v).toFixed(2)}€`, name === "ventas" ? "Real" : name === "predicted" ? "Predicción" : name] : [null]}
                labelFormatter={l => `Fecha: ${l}`}
              />
              <Area dataKey="high" stroke="none" fill="hsl(var(--primary) / 0.12)" name="IC alto" legendType="none" connectNulls />
              <Area dataKey="low" stroke="none" fill="white" name="IC bajo" legendType="none" connectNulls />
              <Line type="monotone" dataKey="ventas" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Real" connectNulls={false} />
              <Line type="monotone" dataKey="predicted" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 3" dot={false} name="Predicción" connectNulls />
              <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Forecast table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Detalle de predicción día a día</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="max-h-64 overflow-y-auto divide-y divide-border">
            {forecast.map(f => (
              <div key={f.date} className="flex items-center justify-between px-4 py-2 text-sm hover:bg-muted/30">
                <span className="text-muted-foreground">{new Date(f.date + "T12:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">({f.low.toFixed(0)} – {f.high.toFixed(0)}€)</span>
                  <span className="font-bold w-20 text-right">{f.predicted.toFixed(2)}€</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
